import { z } from 'zod';
import { numberingSchemeSchema, spotNumber } from './numbering';
import type { ZoneSetting } from './settings';

export const LAYOUT_ELEMENT_KINDS = ['deck', 'bar', 'kitchen', 'wall'] as const;

// A band drawn beyond row 1: the pilot's sea. A venue without one says null.
export const LAYOUT_EDGES = ['sea'] as const;

export const layoutEdgeSchema = z.enum(LAYOUT_EDGES);

export type LayoutEdge = z.infer<typeof layoutEdgeSchema>;

export const layoutElementKindSchema = z.enum(LAYOUT_ELEMENT_KINDS);

export type LayoutElementKind = z.infer<typeof layoutElementKindSchema>;

// Non-spot cells only; every other cell is empty. Spots come from the blocks.
export const layoutElementSchema = z.object({
  gridRow: z.number().int().min(1),
  gridCol: z.number().int().min(1),
  kind: layoutElementKindSchema,
});

export type LayoutElement = z.infer<typeof layoutElementSchema>;

// A rectangular run of spots: cols radiate outward, row 1 is nearest the front.
export const spotBlockSchema = z.object({
  side: z.number().int().min(0),
  cols: z.number().int().min(1),
  rows: z.number().int().min(1),
  zoneKeyByRow: z.array(z.string().min(1)).min(1),
  gridRowOfRow1: z.number().int().min(1),
  gridColOfCol0: z.number().int().min(1),
  gridColStep: z.union([z.literal(1), z.literal(-1)]),
  gridRowStep: z.union([z.literal(1), z.literal(-1)]),
});

export type SpotBlock = z.infer<typeof spotBlockSchema>;

// A spot the owner painted: it carries its own cell, number and zone. §18
export const placedSpotSchema = z.object({
  gridRow: z.number().int().min(1),
  gridCol: z.number().int().min(1),
  number: z.number().int().min(1),
  zoneKey: z.string().min(1),
});

export type PlacedSpot = z.infer<typeof placedSpotSchema>;

// Blocks generate spots from a scheme; spots are painted one by one. Either may
// be empty: a seeded venue uses blocks, the editor's output uses spots. §18
export const layoutConfigSchema = z.object({
  gridRows: z.number().int().min(1),
  gridCols: z.number().int().min(1),
  numbering: numberingSchemeSchema.default({ kind: 'sequential', start: 1 }),
  frontEdge: layoutEdgeSchema.nullish().default(null),
  blocks: z.array(spotBlockSchema).default([]),
  spots: z.array(placedSpotSchema).default([]),
  elements: z.array(layoutElementSchema),
});

export type LayoutConfig = z.infer<typeof layoutConfigSchema>;

// A painted spot has no place in a scheme, so it carries no side/col/row.
export type BuiltSpot = {
  readonly number: number;
  readonly zoneKey: string;
  readonly gridRow: number;
  readonly gridCol: number;
  readonly side?: number;
  readonly col?: number;
  readonly row?: number;
};

// Pure: seed, layout editor, and tests all derive spots from config this way.
export function buildSpots(config: LayoutConfig): readonly BuiltSpot[] {
  const spots: BuiltSpot[] = [];
  let ordinal = 0;
  for (const block of config.blocks) {
    if (block.zoneKeyByRow.length !== block.rows) {
      throw new RangeError(`block needs one zone key per row, got ${block.zoneKeyByRow.length}`);
    }
    for (let col = 0; col < block.cols; col += 1) {
      for (let row = 1; row <= block.rows; row += 1) {
        const zoneKey = block.zoneKeyByRow[row - 1] as string;
        const at = { side: block.side, col, row, ordinal };
        spots.push({
          number: spotNumber(config.numbering, at),
          zoneKey,
          gridRow: block.gridRowOfRow1 + (row - 1) * block.gridRowStep,
          gridCol: block.gridColOfCol0 + col * block.gridColStep,
          side: block.side,
          col,
          row,
        });
        ordinal += 1;
      }
    }
  }
  for (const painted of config.spots) spots.push({ ...painted });
  assertPlaceable(config, spots);
  return spots;
}

function assertPlaceable(config: LayoutConfig, spots: readonly BuiltSpot[]): void {
  const numbers = new Set<number>();
  const cells = new Set<string>();
  const taken = new Set(config.elements.map((e) => `${e.gridRow}:${e.gridCol}`));
  for (const spot of spots) {
    const cell = `${spot.gridRow}:${spot.gridCol}`;
    if (numbers.has(spot.number)) throw new RangeError(`duplicate spot number ${spot.number}`);
    if (cells.has(cell)) throw new RangeError(`two spots share grid cell ${cell}`);
    if (taken.has(cell)) throw new RangeError(`spot ${spot.number} sits on an element at ${cell}`);
    if (spot.gridRow > config.gridRows || spot.gridCol > config.gridCols) {
      throw new RangeError(`spot ${spot.number} falls outside the ${config.gridRows}-row grid`);
    }
    if (spot.gridRow < 1 || spot.gridCol < 1) {
      throw new RangeError(`spot ${spot.number} falls outside the grid at ${cell}`);
    }
    numbers.add(spot.number);
    cells.add(cell);
  }
}

// A grid nobody could draw or read: the editor's own outer bounds. §18
export const MAX_GRID_SIDE = 60;

export const MAX_LAYOUT_SPOTS = 2000;

// What the layout editor holds and saves: painted cells on a plain grid. §18
export const layoutDraftSchema = z.object({
  gridRows: z.number().int().min(1).max(MAX_GRID_SIDE),
  gridCols: z.number().int().min(1).max(MAX_GRID_SIDE),
  frontEdge: layoutEdgeSchema.nullable().default(null),
  spots: z.array(placedSpotSchema).max(MAX_LAYOUT_SPOTS),
  elements: z.array(layoutElementSchema).max(MAX_GRID_SIDE * MAX_GRID_SIDE),
});

export type LayoutDraft = z.infer<typeof layoutDraftSchema>;

// A saved draft is a config with no blocks: every spot carries its own cell.
export function configOfDraft(draft: LayoutDraft): LayoutConfig {
  return {
    gridRows: draft.gridRows,
    gridCols: draft.gridCols,
    numbering: { kind: 'sequential', start: 1 },
    frontEdge: draft.frontEdge,
    blocks: [],
    spots: draft.spots,
    elements: draft.elements,
  };
}

// A seeded block layout opens in the editor as the cells it generates. §18
export function draftOfConfig(config: LayoutConfig): LayoutDraft {
  const spots = buildSpots(config).map((spot) => ({
    gridRow: spot.gridRow,
    gridCol: spot.gridCol,
    number: spot.number,
    zoneKey: spot.zoneKey,
  }));
  const cells = [...spots, ...config.elements];
  return {
    gridRows: Math.max(config.gridRows, ...cells.map((cell) => cell.gridRow)),
    gridCols: Math.max(config.gridCols, ...cells.map((cell) => cell.gridCol)),
    frontEdge: config.frontEdge ?? null,
    spots,
    elements: config.elements,
  };
}

// The next painted spot's number; an unzoned draft is counted the same. §18
export function nextNumberOf(spots: readonly { readonly number: number }[]): number {
  return spots.reduce((highest, spot) => Math.max(highest, spot.number), 0) + 1;
}

// A canvas for a venue that has never drawn one; the owner resizes from here.
export const EMPTY_LAYOUT_DRAFT: LayoutDraft = {
  gridRows: 8,
  gridCols: 12,
  frontEdge: null,
  spots: [],
  elements: [],
};

// §18: an owner drawing a floor starts from another of theirs, not from none.
export const copyLayoutRequestSchema = z.object({ fromVenueId: z.uuid() });

export type CopyLayoutRequest = z.infer<typeof copyLayoutRequestSchema>;

// A language the source never named its zones in reads in the source's. §18
export function zonesForLanguages(
  zones: readonly ZoneSetting[],
  languages: readonly string[],
  fallback: string,
): ZoneSetting[] {
  return zones.map((zone) => ({
    ...zone,
    names: Object.fromEntries(
      languages.map((language) => [language, zone.names[language] ?? zone.names[fallback] ?? '']),
    ),
  }));
}
